/* Catalog Templates Hub — index page linking to the 5 mockup screens.
 * Pure static preview, no auth required, no data fetching. */
import { Link } from "react-router-dom";
import { useCatalogFonts } from "./_fonts";
import { ArrowUpRight, Sliders, Grid3x3, Sparkles, Table, Gem } from "lucide-react";

const TILES = [
  {
    to: "/preview/catalog/designer",
    name: "Catalog Designer",
    role: "Seller-side control panel",
    desc: "Where sellers pick their template, theme tokens, and module toggles. Side-by-side live preview pane.",
    icon: Sliders,
    bg: "#0a1f1c", accent: "#e8ff3a", text: "#ffffff",
    fontHead: "'Cabinet Grotesk', sans-serif",
  },
  {
    to: "/preview/catalog/linesheet",
    name: "Linesheet",
    role: "Template 1 — Default",
    desc: "Utilitarian grid for many SKUs. Wellness skincare brand sample. Cream + sage palette.",
    icon: Grid3x3,
    bg: "#F9F6F0", accent: "#849C8A", text: "#1A2E22",
    fontHead: "'Work Sans', sans-serif",
  },
  {
    to: "/preview/catalog/editorial",
    name: "Editorial",
    role: "Template 2 — Premium",
    desc: "Magazine-style layout for lifestyle brands. Gourmet fine-dining sample. Aubergine + champagne.",
    icon: Sparkles,
    bg: "#2A1B2E", accent: "#E8DCC4", text: "#F7F3EB",
    fontHead: "'Playfair Display', serif",
  },
  {
    to: "/preview/catalog/catalog-sheet",
    name: "Catalog Sheet",
    role: "Template 3 — B2B Utility",
    desc: "Dense data table with quick-order paste. Industrial hardware sample. Slate + safety orange.",
    icon: Table,
    bg: "#F1F5F9", accent: "#FF5A1F", text: "#0F172A",
    fontHead: "'Chivo', sans-serif",
  },
  {
    to: "/preview/catalog/showcase",
    name: "Showcase",
    role: "Template 4 — Artisanal",
    desc: "Gallery-feel single-collection layout. Ceramics maker sample. Ivory + terracotta.",
    icon: Gem,
    bg: "#FAF6F1", accent: "#C25E4B", text: "#2D2422",
    fontHead: "'Bodoni Moda', serif",
  },
];

export default function CatalogTemplatesHub() {
  useCatalogFonts();
  return (
    <div className="min-h-screen bg-[#0e0e10] text-white" style={{ fontFamily: "'Satoshi', sans-serif" }} data-testid="catalog-templates-hub">
      <div className="max-w-6xl mx-auto px-8 py-16">
        <div className="text-[11px] mono uppercase tracking-[0.24em] text-white/40" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          verqo · catalog mockups · preview
        </div>
        <h1 className="mt-3 text-5xl md:text-6xl tracking-tight leading-[1.02]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 700 }}>
          Catalog templates — visual review
        </h1>
        <p className="mt-5 max-w-2xl text-white/65 text-base leading-relaxed">
          Static, click-through mockups of the 4 buyer-facing templates plus the seller-side Catalog Designer.
          No backend wiring yet — purely for visual approval before we start building. Each opens in a new tab.
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {TILES.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              target="_blank"
              rel="noreferrer"
              className="group block rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-colors"
              data-testid={`tile-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ background: t.bg, color: t.text }}
            >
              <div className="p-8 min-h-[260px] flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <span
                      className="w-10 h-10 rounded-lg grid place-items-center"
                      style={{ background: t.accent, color: t.bg }}
                    >
                      <t.icon className="w-5 h-5"/>
                    </span>
                    <div className="text-[10px] uppercase tracking-[0.22em]" style={{ opacity: 0.55, fontFamily: "'JetBrains Mono', monospace" }}>
                      {t.role}
                    </div>
                  </div>
                  <h2 className="mt-6 text-3xl tracking-tight" style={{ fontFamily: t.fontHead, fontWeight: 700 }}>
                    {t.name}
                  </h2>
                  <p className="mt-2.5 text-sm leading-relaxed" style={{ opacity: 0.7 }}>{t.desc}</p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-medium group-hover:translate-x-0.5 transition-transform" style={{ color: t.accent }}>
                  Open preview <ArrowUpRight className="w-3.5 h-3.5"/>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-14 rounded-xl border border-white/10 p-5 text-sm text-white/65 leading-relaxed">
          <strong className="text-white">How to review:</strong> open each tile, judge the overall vibe + layout +
          density. Don&apos;t worry about pixel-perfection — what you&apos;re approving is the <em>direction</em>: do these 4
          templates feel distinctly different and do they match the business models we described? Tell me what to keep,
          swap, or kill.
        </div>
      </div>
    </div>
  );
}
